import React, { useState, useEffect, useRef, useMemo } from "react";
import { Col, Row, Form, Button, Alert } from "react-bootstrap";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const suggestionTypes = [
  { value: "url_action", label: "URL Action" },
  { value: "dialer_action", label: "Dialer Action" },
  { value: "share_location", label: "Share Location" },
  { value: "calendar_event", label: "Calendar Event" },
  { value: "reply", label: "Reply" },
  { value: "view_location_query", label: "View Location by Query" },
  { value: "view_location_latlong", label: "View Location by Lat/Long" },
];

const applicationOptions = [
  { label: "BROWSER", value: "Browser" },
  { label: "WEBVIEW_FULL", value: "Webview-Full" },
  { label: "WEBVIEW_HALF", value: "Webview-Half" },
  { label: "WEBVIEW_TALL", value: "Webview-Tall" },
];

const getEmptySuggestion = () => ({
  suggestionType: "",
  text: "",
  action: {},
});

const RcsSuggestionsField = ({
  suggestion,
  setSuggestion,
  addSuggestion,
  idPrefix = "",
  suggestions = [],
  onEdit = null,
  onDelete = null,
  editingIndex = null,
}) => {
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const selectRef = useRef();

  const emptySuggestion = useMemo(() => getEmptySuggestion(), []);

  useEffect(() => {
    if (Object.keys(touchedFields).length > 0) {
      validateSuggestionFields();
    }
  }, [suggestion]);

  useEffect(() => {
    setTouchedFields({});
    setFieldErrors({});
  }, [suggestion.suggestionType]);

  const update = (key, val) => {
    setSuggestion({ ...suggestion, [key]: val });
  };

  const updateNestedAction = (parentKey, childKey, val) => {
    const currentAction = suggestion.action || {};
    const currentParent = currentAction[parentKey] || {};
    const updatedParent = { ...currentParent, [childKey]: val };
    const updatedAction = { ...currentAction, [parentKey]: updatedParent };
    setSuggestion({ ...suggestion, action: updatedAction });
  };

  const updateUrlActionApplication = (val) => {
    const currentAction = suggestion.action || {};
    const updatedAction = { ...currentAction, application: val };
    setSuggestion({ ...suggestion, action: updatedAction });
  };

  const validateLatitude = (value) => {
    if (value === "" || value === null || isNaN(value)) return "";
    if (value < -90 || value > 90) return "Latitude must be between -90 and 90";
    return "";
  };
  const validateLongitude = (value) => {
    if (value === "" || value === null || isNaN(value)) return "";
    if (value < -180 || value > 180)
      return "Longitude must be between -180 and 180";
    return "";
  };

  const handleLatitudeChange = (e) => {
    const val = e.target.value;
    let floatVal = parseFloat(val);
    if (val === "") floatVal = "";
    updateNestedAction("viewLocationAction", "latLong", {
      ...suggestion.action?.viewLocationAction?.latLong,
      latitude: val === "" ? "" : floatVal,
      longitude:
        suggestion.action?.viewLocationAction?.latLong?.longitude ?? "",
    });
    setTouchedFields((prev) => ({ ...prev, latitude: true }));
    setFieldErrors((prev) => ({
      ...prev,
      latitude: validateLatitude(floatVal),
    }));
  };

  const handleLongitudeChange = (e) => {
    const val = e.target.value;
    let floatVal = parseFloat(val);
    if (val === "") floatVal = "";
    updateNestedAction("viewLocationAction", "latLong", {
      ...suggestion.action?.viewLocationAction?.latLong,
      latitude: suggestion.action?.viewLocationAction?.latLong?.latitude ?? "",
      longitude: val === "" ? "" : floatVal,
    });
    setTouchedFields((prev) => ({ ...prev, longitude: true }));
    setFieldErrors((prev) => ({
      ...prev,
      longitude: validateLongitude(floatVal),
    }));
  };

  const validateSuggestionFields = (forceValidateAll = false) => {
    const errors = {};
    const type = suggestion.suggestionType;

    if (!type) {
      errors.suggestionType = "Type is required";
    }
    if (!suggestion.text || suggestion.text.trim() === "") {
      errors.text = "Text is required";
    }

    if (type === "url_action") {
      const url = suggestion.action?.openUrlAction?.url || "";
      if (forceValidateAll || touchedFields.url) {
        if (!url.trim()) {
          errors.url = "URL is required";
        } else {
          const urlPattern =
            /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/;
          if (!urlPattern.test(url.trim())) {
            errors.url = "Invalid URL format";
          }
        }
      }
    } else if (type === "dialer_action") {
      const phone = suggestion.action?.dialAction?.phoneNumber || "";
      if (forceValidateAll || touchedFields.phoneNumber) {
        if (!phone.trim()) {
          errors.phoneNumber = "Phone number is required";
        } else {
          const digits = phone.replace(/^\+/, "");
          if (!/^\d{7,15}$/.test(digits)) {
            errors.phoneNumber = "Invalid phone number";
          }
        }
      }
    } else if (type === "view_location_latlong") {
      const lat = suggestion.action?.viewLocationAction?.latLong?.latitude;
      const long = suggestion.action?.viewLocationAction?.latLong?.longitude;
      if (forceValidateAll || touchedFields.latitude) {
        if (lat === "" || lat === undefined || lat === null) {
          errors.latitude = "Latitude is required";
        } else if (isNaN(lat) || lat < -90 || lat > 90) {
          errors.latitude = "Latitude must be between -90 and 90";
        }
      }
      if (forceValidateAll || touchedFields.longitude) {
        if (long === "" || long === undefined || long === null) {
          errors.longitude = "Longitude is required";
        } else if (isNaN(long) || long < -180 || long > 180) {
          errors.longitude = "Longitude must be between -180 and 180";
        }
      }
    } else if (type === "view_location_query") {
      const query = suggestion.action?.viewLocationAction?.query || "";
      if (forceValidateAll || touchedFields.locationQuery) {
        if (!query.trim()) {
          errors.locationQuery = "Location query is required";
        }
      }
    } else if (type === "calendar_event") {
      const startTime =
        suggestion.action?.createCalendarEventAction?.startTime || "";
      const endTime =
        suggestion.action?.createCalendarEventAction?.endTime || "";
      const title = suggestion.action?.createCalendarEventAction?.title || "";

      if (forceValidateAll || touchedFields.startTime) {
        if (!startTime) {
          errors.startTime = "Start time is required";
        }
      }
      if (forceValidateAll || touchedFields.endTime) {
        if (!endTime) {
          errors.endTime = "End time is required";
        } else if (startTime && endTime && startTime > endTime) {
          errors.endTime = "End time must be after start time";
        }
      }
      if (forceValidateAll || touchedFields.eventTitle) {
        if (!title.trim()) {
          errors.eventTitle = "Event title is required";
        }
      }
    }

    setFieldErrors(errors);
    return errors;
  };

  const handleSuggestionTypeChange = (opt) => {
    update("suggestionType", opt?.value);
    setFieldErrors({});
    setTouchedFields({});
  };

  const renderSuggestionFields = () => {
    const suggestionType = suggestion.suggestionType;
    switch (suggestionType) {
      case "url_action":
        return (
          <>
            <Form.Group as={Col} md={4} className="mb-0">
              <Form.Label htmlFor={`${idPrefix}url`}>
                URL <span className="text-danger"> * </span>
              </Form.Label>
              <Form.Control
                id={`${idPrefix}url`}
                name="url"
                type="text"
                placeholder="https://example.com"
                value={suggestion.action?.openUrlAction?.url || ""}
                onChange={(e) => {
                  updateNestedAction("openUrlAction", "url", e.target.value);
                  setTouchedFields((prev) => ({ ...prev, url: true }));
                }}
                isInvalid={!!fieldErrors.url}
              />
              {fieldErrors.url && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.url}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-0">
              <Form.Label htmlFor={`${idPrefix}application`}>
                Application
              </Form.Label>
              <Select
                inputId={`${idPrefix}application`}
                name="application"
                options={applicationOptions}
                value={applicationOptions.find(
                  (opt) =>
                    opt.value ===
                    (suggestion.action?.application ||
                      applicationOptions[0].value)
                )}
                onChange={(opt) => {
                  updateUrlActionApplication(opt ? opt.value : "");
                }}
                placeholder="Select Application"
              />
            </Form.Group>
          </>
        );
      case "dialer_action":
        return (
          <Form.Group as={Col} md={4} className="mb-0">
            <Form.Label htmlFor={`${idPrefix}phoneNumber`}>
              Phone Number <span className="text-danger"> * </span>
            </Form.Label>
            <PhoneInput
              country={"in"}
              value={suggestion.action?.dialAction?.phoneNumber || ""}
              onChange={(value) => {
                updateNestedAction("dialAction", "phoneNumber", `+${value}`);
                setTouchedFields((prev) => ({ ...prev, phoneNumber: true }));
              }}
              inputProps={{
                name: "phoneNumber",
                id: `${idPrefix}phoneNumber`,
                required: true,
                autoFocus: false,
              }}
              inputStyle={{ width: "100%" }}
              containerStyle={{ width: "100%" }}
              placeholder="Phone number"
              isInvalid={!!fieldErrors.phoneNumber}
            />
            {fieldErrors.phoneNumber && (
              <div className="invalid-feedback d-block">
                {fieldErrors.phoneNumber}
              </div>
            )}
          </Form.Group>
        );
      case "view_location_latlong":
        return (
          <>
            <Form.Group as={Col} md={4} className="mb-0">
              <Form.Label htmlFor={`${idPrefix}latitude`}>
                Latitude <span className="text-danger"> * </span>
              </Form.Label>
              <Form.Control
                id={`${idPrefix}latitude`}
                name="latitude"
                type="number"
                step="any"
                placeholder="19.017"
                value={
                  suggestion.action?.viewLocationAction?.latLong?.latitude === 0
                    ? 0
                    : suggestion.action?.viewLocationAction?.latLong
                        ?.latitude || ""
                }
                onChange={handleLatitudeChange}
                min={-90}
                max={90}
                isInvalid={!!fieldErrors.latitude}
                onBlur={() =>
                  setTouchedFields((prev) => ({ ...prev, latitude: true }))
                }
              />
              {fieldErrors.latitude && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.latitude}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-0">
              <Form.Label htmlFor={`${idPrefix}longitude`}>
                Longitude <span className="text-danger"> * </span>
              </Form.Label>
              <Form.Control
                id={`${idPrefix}longitude`}
                name="longitude"
                type="number"
                step="any"
                placeholder="72.8304"
                value={
                  suggestion.action?.viewLocationAction?.latLong?.longitude ===
                  0
                    ? 0
                    : suggestion.action?.viewLocationAction?.latLong
                        ?.longitude || ""
                }
                onChange={handleLongitudeChange}
                min={-180}
                max={180}
                isInvalid={!!fieldErrors.longitude}
                onBlur={() =>
                  setTouchedFields((prev) => ({ ...prev, longitude: true }))
                }
              />
              {fieldErrors.longitude && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.longitude}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-0">
              <Form.Label htmlFor={`${idPrefix}locationLabel`}>
                Location Label
              </Form.Label>
              <Form.Control
                id={`${idPrefix}locationLabel`}
                name="locationLabel"
                type="text"
                placeholder="Mumbai"
                value={suggestion.action?.viewLocationAction?.label || ""}
                onChange={(e) => {
                  updateNestedAction(
                    "viewLocationAction",
                    "label",
                    e.target.value
                  );
                }}
              />
            </Form.Group>
          </>
        );
      case "view_location_query":
        return (
          <Form.Group as={Col} md={4} className="mb-0">
            <Form.Label htmlFor={`${idPrefix}locationQuery`}>
              Location Query <span className="text-danger"> * </span>
            </Form.Label>
            <Form.Control
              id={`${idPrefix}locationQuery`}
              name="locationQuery"
              type="text"
              placeholder="Mumbai"
              value={suggestion.action?.viewLocationAction?.query || ""}
              onChange={(e) => {
                updateNestedAction(
                  "viewLocationAction",
                  "query",
                  e.target.value
                );
                setTouchedFields((prev) => ({
                  ...prev,
                  locationQuery: true,
                }));
              }}
              isInvalid={!!fieldErrors.locationQuery}
            />
            {fieldErrors.locationQuery && (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.locationQuery}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        );
      case "calendar_event":
        return (
          <>
            <Form.Group as={Col} md={4} className="mb-0">
              <Form.Label htmlFor={`${idPrefix}startTime`}>
                Start Time <span className="text-danger"> * </span>
              </Form.Label>
              <Form.Control
                id={`${idPrefix}startTime`}
                name="startTime"
                type="datetime-local"
                value={
                  suggestion.action?.createCalendarEventAction?.startTime
                    ? suggestion.action.createCalendarEventAction.startTime.replace(
                        " ",
                        "T"
                      )
                    : ""
                }
                onChange={(e) => {
                  updateNestedAction(
                    "createCalendarEventAction",
                    "startTime",
                    e.target.value.replace("T", " ")
                  );
                  setTouchedFields((prev) => ({
                    ...prev,
                    startTime: true,
                  }));
                }}
                isInvalid={!!fieldErrors.startTime}
              />
              {fieldErrors.startTime && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.startTime}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-0">
              <Form.Label htmlFor={`${idPrefix}endTime`}>
                End Time <span className="text-danger"> * </span>
              </Form.Label>
              <Form.Control
                id={`${idPrefix}endTime`}
                name="endTime"
                type="datetime-local"
                value={
                  suggestion.action?.createCalendarEventAction?.endTime
                    ? suggestion.action.createCalendarEventAction.endTime.replace(
                        " ",
                        "T"
                      )
                    : ""
                }
                onChange={(e) => {
                  updateNestedAction(
                    "createCalendarEventAction",
                    "endTime",
                    e.target.value.replace("T", " ")
                  );
                  setTouchedFields((prev) => ({
                    ...prev,
                    endTime: true,
                  }));
                }}
                isInvalid={!!fieldErrors.endTime}
              />
              {fieldErrors.endTime && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.endTime}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-0">
              <Form.Label htmlFor={`${idPrefix}eventTitle`}>
                Event Title <span className="text-danger"> * </span>
              </Form.Label>
              <Form.Control
                id={`${idPrefix}eventTitle`}
                name="eventTitle"
                type="text"
                placeholder="campaign date"
                value={
                  suggestion.action?.createCalendarEventAction?.title || ""
                }
                onChange={(e) => {
                  updateNestedAction(
                    "createCalendarEventAction",
                    "title",
                    e.target.value
                  );
                  setTouchedFields((prev) => ({
                    ...prev,
                    eventTitle: true,
                  }));
                }}
                isInvalid={!!fieldErrors.eventTitle}
              />
              {fieldErrors.eventTitle && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.eventTitle}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-0">
              <Form.Label htmlFor={`${idPrefix}eventDescription`}>
                Event Description
              </Form.Label>
              <Form.Control
                id={`${idPrefix}eventDescription`}
                name="eventDescription"
                type="text"
                placeholder="all rcs campaign"
                value={
                  suggestion.action?.createCalendarEventAction?.description ||
                  ""
                }
                onChange={(e) => {
                  updateNestedAction(
                    "createCalendarEventAction",
                    "description",
                    e.target.value
                  );
                }}
              />
            </Form.Group>
          </>
        );
      case "share_location":
      case "reply":
      default:
        return null;
    }
  };

  const getFieldError = (field) => fieldErrors[field];

  const handleAddSuggestion = (e) => {
    e.preventDefault();

    const allTouched = {
      suggestionType: true,
      text: true,
    };

    if (suggestion.suggestionType === "url_action") {
      allTouched.url = true;
    }
    if (suggestion.suggestionType === "dialer_action") {
      allTouched.phoneNumber = true;
    }
    if (suggestion.suggestionType === "view_location_latlong") {
      allTouched.latitude = true;
      allTouched.longitude = true;
    }
    if (suggestion.suggestionType === "view_location_query") {
      allTouched.locationQuery = true;
    }
    if (suggestion.suggestionType === "calendar_event") {
      allTouched.startTime = true;
      allTouched.endTime = true;
      allTouched.eventTitle = true;
    }

    setTouchedFields(allTouched);

    const errors = validateSuggestionFields(true);

    if (Object.keys(errors).length === 0) {
      addSuggestion();
      setFieldErrors({});
      setTouchedFields({});
      setSuggestion(emptySuggestion);
      if (selectRef.current) {
        selectRef.current.clearValue();
      }
    }
  };

  return (
    <>
      <div>
        <Row className="align-items-end d-flex flex-column g-2 row">
          <Col md={12}>
            <Row className="g-2 flex-wrap">
              <Form.Group as={Col} md={4} className="mb-0">
                <Form.Label htmlFor={`${idPrefix}suggestionType`}>
                  Type <span className="text-danger"> * </span>
                </Form.Label>
                <Select
                  ref={selectRef}
                  inputId={`${idPrefix}suggestionType`}
                  name="suggestionType"
                  options={suggestionTypes}
                  value={suggestionTypes.find(
                    (s) => s.value === suggestion.suggestionType
                  )}
                  onChange={handleSuggestionTypeChange}
                  className={
                    getFieldError("suggestionType") ? "is-invalid" : ""
                  }
                />
                {getFieldError("suggestionType") && (
                  <div className="invalid-feedback d-block">
                    {getFieldError("suggestionType")}
                  </div>
                )}
              </Form.Group>
              <Form.Group as={Col} md={4} className="mb-0">
                <Form.Label htmlFor={`${idPrefix}suggestionText`}>
                  Text <span className="text-danger"> * </span>
                </Form.Label>
                <Form.Control
                  id={`${idPrefix}suggestionText`}
                  name="suggestionText"
                  type="text"
                  value={suggestion.text || ""}
                  onChange={(e) => {
                    update("text", e.target.value);
                    setTouchedFields((prev) => ({
                      ...prev,
                      text: true,
                    }));
                  }}
                  isInvalid={!!getFieldError("text")}
                />
                {getFieldError("text") && (
                  <Form.Control.Feedback type="invalid">
                    {getFieldError("text")}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Form.Group as={Col} md={4} className="mb-0">
                <Form.Label htmlFor={`${idPrefix}postbackData`}>
                  Postback Data
                </Form.Label>
                <Form.Control
                  id={`${idPrefix}postbackData`}
                  name="postbackData"
                  type="text"
                  placeholder="Enter postback data"
                  value={suggestion.postbackData || ""}
                  onChange={(e) => {
                    update("postbackData", e.target.value);
                  }}
                />
              </Form.Group>
              {renderSuggestionFields()}
            </Row>
            {Object.keys(fieldErrors).length > 0 &&
              Object.keys(touchedFields).length > 0 && (
                <Alert variant="danger" className="mt-2 mb-0 py-1 px-2">
                  Please fix the errors above before adding the suggestion.
                </Alert>
              )}
          </Col>
          <Col md={4} className="mb-0 d-flex align-items-end">
            <Button
              onClick={handleAddSuggestion}
              className="w-100"
              name="addSuggestion"
              type="button"
            >
              {editingIndex !== null ? "Update" : "Add"}
            </Button>
          </Col>
        </Row>
      </div>
      {suggestions && suggestions.length > 0 && (onEdit || onDelete) && (
        <div className="card light p-3 mt-3 m-0">
          <ul className="list-group mb-0">
            {suggestions.map((s, idx) => (
              <li
                key={idx}
                className={`list-group-item border rounded-2 mb-2 d-flex justify-content-between align-items-center${
                  editingIndex === idx ? " active" : ""
                }`}
                style={
                  editingIndex === idx
                    ? {
                        background: "rgb(38 43 64)",
                        border: "2px solid #4f5bd5",
                      }
                    : { background: "rgb(138 156 166)" }
                }
              >
                <span style={editingIndex !== idx ? { color: "#000000" } : {}}>
                  <strong>{s.text}</strong>
                  {s.suggestionType ? ` (${s.suggestionType})` : ""}
                  {s.action?.openUrlAction?.url
                    ? ` - URL: ${s.action.openUrlAction.url}`
                    : ""}
                  {s.action?.application
                    ? ` - Application: ${s.action.application}`
                    : ""}
                  {s.action?.dialAction?.phoneNumber
                    ? ` - Phone: ${s.action.dialAction.phoneNumber}`
                    : ""}
                  {s.action?.viewLocationAction?.query
                    ? ` - Query: ${s.action.viewLocationAction.query}`
                    : ""}
                  {s.action?.viewLocationAction?.latLong
                    ? ` - Lat: ${s.action.viewLocationAction.latLong.latitude}, Long: ${s.action.viewLocationAction.latLong.longitude}`
                    : ""}
                  {s.action?.createCalendarEventAction?.title
                    ? ` - Event: ${s.action.createCalendarEventAction.title}`
                    : ""}
                </span>
                <span className="py-2 d-flex gap-2">
                  {onEdit && editingIndex !== idx && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1"
                      onClick={() => onEdit(idx)}
                      type="button"
                      disabled={editingIndex === idx}
                    >
                      Edit
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => onDelete(idx)}
                      type="button"
                    >
                      Delete
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default RcsSuggestionsField;
